import { useMemo, useState } from "react";
import { useDocuments } from "../../lib/documents";
import { useCan } from "../../lib/auth";

interface DocumentReferencePickerProps {
  selectedIds: number[];
  onChange: (next: number[]) => void;
  /** Documents to exclude from the choices (e.g. the current doc when picking "see also"). */
  excludeIds?: number[];
  /** Heading/hint text. */
  hint?: string;
}

/**
 * Multi-select picker for documents — shared between the document editor
 * (for "see also" links) and the event editor (for "materials"). Editors
 * with `documents.edit_any` use `?all=true` so drafts are pickable too;
 * everyone else only sees published documents (avoids a 403 on `all`).
 */
export function DocumentReferencePicker({
  selectedIds,
  onChange,
  excludeIds = [],
  hint,
}: DocumentReferencePickerProps) {
  const [search, setSearch] = useState("");
  const canEditAny = useCan("documents.edit_any");
  const allQ = useDocuments(canEditAny ? { all: true, limit: 200 } : { limit: 200 });
  const allDocs = allQ.data?.documents ?? [];

  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allDocs
      .filter((d) => !exclude.has(d.id))
      .filter((d) => {
        if (!q) return true;
        return (
          d.title.toLowerCase().includes(q) ||
          (d.subtitle?.toLowerCase().includes(q) ?? false) ||
          (d.excerpt?.toLowerCase().includes(q) ?? false)
        );
      });
  }, [allDocs, exclude, search]);

  const selectedDocs = useMemo(
    () => allDocs.filter((d) => selected.has(d.id)),
    [allDocs, selected],
  );

  const toggle = (id: number) => {
    if (selected.has(id)) onChange(selectedIds.filter((i) => i !== id));
    else onChange([...selectedIds, id]);
  };

  return (
    <div className="flex flex-col gap-2">
      {hint && (
        <p className="font-grotesk text-[0.7rem] tracking-[0.04em] text-[var(--color-ink-mute)]">
          {hint}
        </p>
      )}

      {selectedDocs.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 mb-1">
          {selectedDocs.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                className="chip is-active"
                onClick={() => toggle(d.id)}
                title="Remove"
              >
                {d.title}
                <span className="ml-1.5 opacity-70">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        className="auth-input"
        type="search"
        placeholder="Search documents…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="border border-[var(--color-rule)] max-h-[260px] overflow-y-auto rounded-[var(--radius-card)]">
        {visible.length === 0 ? (
          <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)] italic p-3">
            No matches.
          </p>
        ) : (
          <ul>
            {visible.map((d) => {
              const isSelected = selected.has(d.id);
              return (
                <li
                  key={d.id}
                  className="border-t border-[var(--color-rule)] first:border-t-0"
                >
                  <button
                    type="button"
                    onClick={() => toggle(d.id)}
                    className="w-full text-left px-3 py-2 hover:bg-[var(--color-vellum-dim)] transition-colors flex items-center gap-3"
                  >
                    <span
                      aria-hidden="true"
                      className="inline-flex items-center justify-center w-4 h-4 border border-[var(--color-ink-mute)] text-[var(--color-violet)] flex-shrink-0 rounded-[var(--radius-xs)]"
                    >
                      {isSelected ? "✓" : ""}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-display text-[1.05rem] leading-[1.2] tracking-tight">
                        {d.title}
                      </span>
                      {d.subtitle && (
                        <span className="block font-grotesk text-[0.75rem] text-[var(--color-ink-mute)] mt-0.5">
                          {d.subtitle}
                        </span>
                      )}
                    </span>
                    <span
                      className={`font-grotesk text-[0.65rem] tracking-[0.14em] uppercase ${
                        d.status === "published"
                          ? "text-[var(--color-violet)]"
                          : "text-[var(--color-ink-mute)]"
                      }`}
                    >
                      {d.status}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
